import { useState, useRef, useEffect } from 'react'

import { useLazyQuery } from '@apollo/client'
import { createId } from '@paralleldrive/cuid2'
import * as Sentry from '@sentry/nextjs'
import { IconPlus } from '@tabler/icons-react'
import { message } from 'antd'

import { WorkerInput } from '@/components/form-components/WorkerInput'
import { WorkerInputWorker } from '@/components/form-components/WorkerInputWorker'
import { Alert } from '@/components/shared-components/BUI/Alert'
import { Button } from '@/components/shared-components/BUI/Button'
import { Form } from '@/components/shared-components/BUI/Form'
import { Modal } from '@/components/shared-components/BUI/Modal'
import { Text } from '@/components/shared-components/BUI/Text'
import { Flexbox } from '@/components/shared-components/Flexbox'
import {
  SignatureBox,
  SigCanvas,
} from '@/components/shared-components/SignatureBox'
import { useWorkerWebContext } from '@/context/WorkerWebContext'
import { GET_SIGNATURE_UPLOAD_URL } from '@/modules/documentSignatures/graphQL'

import {
  GetPresignedUrlForSignatureUiQuery,
  GetPresignedUrlForSignatureUiQueryVariables,
} from 'types/api.graphql'

type FormFields = {
  key: string
  signatureUrl: string
  workerId: string
}

const AddManualSignatureForm = ({
  title,
  onSubmit,
  projectId,
}: {
  title: string
  onSubmit: (values: FormFields) => Promise<boolean>
  initialValues?: FormFields
  projectId: string
}) => {
  const [form] = Form.useForm()

  const { project: workerWebProject } = useWorkerWebContext() ?? {}
  const [showModal, setShowModal] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const existingSignatureData = Form.useWatch('signature', form)
  const canvasRef = useRef<SigCanvas>(null)

  useEffect(() => {
    if (!showModal) {
      form.resetFields()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal])

  const [getSignatureUploadUrl] = useLazyQuery<
    GetPresignedUrlForSignatureUiQuery,
    GetPresignedUrlForSignatureUiQueryVariables
  >(GET_SIGNATURE_UPLOAD_URL)

  // Unified function to upload signature
  const uploadSignature = async (
    requireSignature: boolean = true
  ): Promise<{ key: string; signatureUrl: string } | null> => {
    // Check if signature is required and canvas is empty
    if (
      requireSignature &&
      (!canvasRef.current || canvasRef.current.isEmpty())
    ) {
      throw new Error('Signature is required')
    }

    // If no signature is provided or canvas is empty, return null
    if (!canvasRef.current || canvasRef.current.isEmpty()) {
      return null
    }

    // Create signature file
    const blob = canvasRef.current.toDataURL('image/png')
    const base64Data = Buffer.from(
      blob.replace(/^data:image\/\w+;base64,/, ''),
      'base64'
    )

    const file = new File([base64Data], `signature-${createId()}.png`, {
      type: 'image/png',
    })

    // Get presigned URL
    const presignedUrlData = await getSignatureUploadUrl({
      variables: {
        id: `signature-${createId()}`,
        filename: `${file.name}.png`,
        filesize: file.size.toString(),
        filetype: file.type,
      },
    })

    const { key, url, viewUrl } = presignedUrlData.data.getPresignedUrl
    const renamedFile = new File([file], key)

    // Upload signature to S3
    await fetch(url, {
      method: 'PUT',
      body: renamedFile,
      headers: { 'Content-Type': file.type },
    })

    return { key, signatureUrl: viewUrl }
  }

  const handleSubmit = async (values: FormFields) => {
    setIsSubmitting(true)
    await form.validateFields()

    try {
      const { key, signatureUrl } = await uploadSignature()

      const response = await onSubmit({
        ...values,
        key,
        signatureUrl,
      })

      if (response) {
        setShowModal(false)
      }

      form.resetFields()
    } catch (error) {
      message.error(
        "Failed to add worker's signature. Please try again or contact support."
      )
      Sentry.captureException(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const validateSignature = () => {
    if (canvasRef.current?.isEmpty()) {
      return Promise.reject('Please have the worker sign above')
    }
    return Promise.resolve()
  }

  const saveSignature = async (signatureData: []) => {
    form.setFieldValue('signature', signatureData)
    await form.validateFields(['signature'])
  }

  return (
    <>
      <Button
        variant="secondary"
        icon={<IconPlus />}
        onClick={() => setShowModal(true)}
        style={{ width: '100%' }}
      >
        Add manual signature
      </Button>
      <Modal
        title={title}
        open={showModal}
        onCancel={() => setShowModal(false)}
        destroyOnClose={true}
      >
        <Form onFinish={handleSubmit} form={form}>
          <Flexbox flexDirection="column">
            <Text variant="label02">Worker name</Text>
            <Form.Item name="workerId">
              {workerWebProject?.publicProject?.id ? (
                <WorkerInputWorker projectId={projectId} hideCurrentWorker />
              ) : (
                <WorkerInput projectId={projectId} />
              )}
            </Form.Item>
            <Form.Item
              name="signature"
              rules={[{ validator: validateSignature }]}
            >
              <SignatureBox
                canvasRef={canvasRef}
                labelOverride="Worker signature"
                saveSignature={saveSignature}
                signatureData={existingSignatureData}
              />
            </Form.Item>

            <Alert
              title="If the worker has not signed on yet, this will count as their sign on."
              variant="info"
              style={{ marginBottom: '1rem' }}
            />

            <Button variant="primary" type="submit" loading={isSubmitting}>
              Record signature
            </Button>
          </Flexbox>
        </Form>
      </Modal>
    </>
  )
}

export default AddManualSignatureForm
